import { cn } from "@/common/helpers/utils";
import { SelectionFooter } from "@/components/selection-footer/selection-footer";
import { RowData, RowSelectionState, Updater } from "@tanstack/react-table";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode } from "react";
import { getSelectableRowSelection } from "../_helpers/get-selectable-row-selection";
import { RowWithIdentifier } from "../_helpers/row-identifier";
import { DataTableInstance, TableBackground, tableBackgroundStyles } from "../types";

export interface CustomSelectionAction<TData = unknown> {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  action: (selection: string[] | TData[]) => Promise<void>;
}

interface DataTableFooterProps<TData extends RowData & RowWithIdentifier> {
  footerComponent?: ReactNode;
  tableBackground?: TableBackground;
  isSelectionEnabled: boolean;
  selectedCount: number;
  internalRowSelection: RowSelectionState;
  customSelectionActions?: CustomSelectionAction<TData>[];
  showClearSelection?: boolean;
  showSelectionCount?: boolean;
  selection: string[] | TData[];
  serverSidePagination: boolean;
  totalItems?: number;
  table: DataTableInstance<TData>;
  setRowSelection?: (updater: Updater<RowSelectionState>) => void;
  onClearSelection?: () => void;
}

export function DataTableFooter<TData extends RowData & RowWithIdentifier>({
  footerComponent,
  tableBackground,
  isSelectionEnabled,
  selectedCount,
  internalRowSelection,
  customSelectionActions,
  showClearSelection,
  showSelectionCount,
  selection,
  serverSidePagination,
  totalItems,
  table,
  setRowSelection,
  onClearSelection,
}: DataTableFooterProps<TData>) {
  const handleCustomAction = async (action: CustomSelectionAction<TData>) => {
    await action.action(selection);
  };

  if (!isSelectionEnabled && !footerComponent) {
    return null;
  }

  const selectableRowSelection =
    isSelectionEnabled && !serverSidePagination ? getSelectableRowSelection(table.getFilteredRowModel().rows) : {};

  return (
    <div className={cn("flex flex-col", tableBackgroundStyles[tableBackground ?? TableBackground.Background])}>
      <AnimatePresence>
        {showClearSelection !== false && isSelectionEnabled && selectedCount > 0 && (
          <motion.div
            key="selection-footer"
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 16, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="bg-muted/30 mt-4 flex w-full shrink-0 items-center justify-between px-4 py-3 lg:px-6"
          >
            <SelectionFooter
              selection={Object.keys(internalRowSelection).filter((key) => internalRowSelection[key])}
              totalCount={serverSidePagination ? (totalItems ?? 0) : Object.keys(selectableRowSelection).length}
              showSelectionCount={showSelectionCount}
              additionalButtons={customSelectionActions?.map((action) => ({
                label: action.label,
                icon: action.icon,
                action: () => handleCustomAction(action),
              }))}
              onClearSelection={() => {
                if (setRowSelection) {
                  setRowSelection({});
                } else {
                  table.resetRowSelection();
                  onClearSelection?.();
                }
              }}
              onSelectAll={
                serverSidePagination
                  ? undefined
                  : () => {
                      if (setRowSelection) {
                        setRowSelection(selectableRowSelection);
                      } else {
                        table.setRowSelection(selectableRowSelection);
                      }
                    }
              }
            />
          </motion.div>
        )}
      </AnimatePresence>

      {footerComponent && <div className="mt-4 shrink-0 px-4 lg:px-6">{footerComponent}</div>}
    </div>
  );
}
